
import { Link } from "react-router-dom";

export default function SnowRemoval() {
  return (
    <div className="min-h-screen bg-[#080808] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#080808]/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-10 h-10 bg-gradient-to-br from-[#00E5FF] to-[#00B8D4] rounded flex items-center justify-center">
              <i className="ri-building-4-line text-xl text-[#080808]"></i>
            </div>
            <span className="font-['Barlow_Condensed'] text-2xl font-bold tracking-wide">SURE KLEEN</span>
          </Link>
          <Link
            to="/"
            className="text-sm font-['Space_Grotesk'] text-white/70 hover:text-[#00E5FF] transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-line mr-2"></i>Back to Home
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden mt-16">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/f53373edeb669e6c3e56d3820d00dc6b.jpg"
            alt="Snow Removal Services"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#080808]"></div>
        </div>
        <div className="relative z-10 text-center max-w-4xl px-6">
          <div className="inline-block px-4 py-2 bg-[#00E5FF]/10 border border-[#00E5FF]/30 rounded-full mb-6">
            <span className="font-['Space_Mono'] text-sm text-[#00E5FF] tracking-wider">SIC 0782</span>
          </div>
          <h1 className="font-['Barlow_Condensed'] text-6xl md:text-7xl font-bold mb-6 tracking-tight">
            Commercial Snow Removal
          </h1>
          <p className="font-['Space_Grotesk'] text-xl text-white/80 max-w-2xl mx-auto leading-relaxed">
            24/7 winter weather response to keep your property safe, accessible, and operational
          </p>
        </div>
      </section>

      {/* What We Deliver */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-['Barlow_Condensed'] text-5xl font-bold mb-16 text-center">
            What We <span className="text-[#00E5FF]">Deliver</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                icon: "ri-truck-line",
                title: "Fleet-Ready Response",
                desc: "Commercial plows, loaders, and salt spreaders deployed within trigger thresholds",
              },
              {
                icon: "ri-24-hours-line",
                title: "24/7 Monitoring",
                desc: "Real-time weather tracking and proactive deployment before accumulation",
              },
              {
                icon: "ri-map-pin-line",
                title: "Priority Routing",
                desc: "Strategic clearing of fire lanes, entrances, and high-traffic areas first",
              },
              {
                icon: "ri-contrast-drop-2-line",
                title: "De-Icing Programs",
                desc: "Pre-treatment and post-storm applications to prevent ice formation",
              },
              {
                icon: "ri-walk-line",
                title: "Sidewalk & Entrance Clearing",
                desc: "Hand shoveling and ice melt application for pedestrian safety",
              },
              {
                icon: "ri-file-list-3-line",
                title: "Storm Documentation",
                desc: "Time-stamped service logs and photo verification for liability protection",
              },
            ].map((item, idx) => (
              <div
                key={idx}
                className="bg-white/5 border border-white/10 rounded-lg p-8 hover:border-[#00E5FF]/50 transition-all group"
              >
                <div className="w-14 h-14 bg-[#00E5FF]/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-[#00E5FF]/20 transition-colors">
                  <i className={`${item.icon} text-3xl text-[#00E5FF]`}></i>
                </div>
                <h3 className="font-['Space_Grotesk'] text-xl font-bold mb-3">{item.title}</h3>
                <p className="text-white/70 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="py-24 px-6 bg-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-['Barlow_Condensed'] text-5xl font-bold mb-8">
            Why It <span className="text-[#00E5FF]">Matters</span>
          </h2>
          <p className="font-['Space_Grotesk'] text-lg text-white/80 leading-relaxed mb-6">
            Winter weather doesn't wait for business hours. Snow and ice create liability risks,
            disrupt operations, and prevent access for employees, customers, and emergency services.
            Reactive snow removal means lost revenue and potential lawsuits.
          </p>
          <p className="font-['Space_Grotesk'] text-lg text-white/80 leading-relaxed">
            Sure Kleen's commercial snow management program provides proactive monitoring, rapid
            response, and comprehensive documentation—keeping your property safe, accessible, and
            compliant throughout winter.
          </p>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-['Barlow_Condensed'] text-5xl font-bold mb-16 text-center">
            Our <span className="text-[#00E5FF]">Process</span>
          </h2>
          <div className="space-y-8">
            {[
              {
                step: "01",
                title: "Pre-Season Planning",
                desc: "Site mapping, priority area identification, trigger threshold agreement, and equipment staging",
              },
              {
                step: "02",
                title: "Weather Monitoring",
                desc: "24/7 meteorological tracking with automated alerts for approaching storm systems",
              },
              {
                step: "03",
                title: "Pre-Treatment",
                desc: "Anti-icing application to parking lots and walkways before precipitation begins",
              },
              {
                step: "04",
                title: "Active Storm Response",
                desc: "Continuous plowing and salting during accumulation to maintain safe conditions",
              },
              {
                step: "05", // Fixed syntax error: removed unexpected "="
                title: "Post-Storm Cleanup",
                desc: "Final clearing, pile relocation, and ice melt application with photo documentation",
              },
            ].map((item, idx) => (
              <div key={idx} className="flex gap-8 items-start group">
                <div className="flex-shrink-0 w-20 h-20 bg-gradient-to-br from-[#00E5FF] to-[#00B8D4] rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <span className="font-['Barlow_Condensed'] text-2xl font-bold text-[#080808]">{item.step}</span>
                </div>
                <div className="flex-1 pt-2">
                  <h3 className="font-['Space_Grotesk'] text-2xl font-bold mb-3">{item.title}</h3>
                  <p className="text-white/70 leading-relaxed text-lg">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Standards & Compliance */}
      <section className="py-24 px-6 bg-white/5">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-['Barlow_Condensed'] text-5xl font-bold mb-16 text-center">
            Standards & <span className="text-[#00E5FF]">Compliance</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: "ri-shield-check-line",
                title: "Fully Insured",
                desc: "Comprehensive liability coverage for property damage and slip-and-fall incidents",
              },
              {
                icon: "ri-file-list-3-line",
                title: "Documented Service",
                desc: "Time-stamped logs and GPS tracking for legal protection and verification",
              },
              {
                icon: "ri-leaf-line",
                title: "Eco-Responsible De-Icing",
                desc: "Calcium chloride and eco-friendly alternatives to minimize environmental impact",
              },
            ].map((item, idx) => (
              <div key={idx} className="text-center">
                <div className="w-20 h-20 bg-[#00E5FF]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${item.icon} text-4xl text-[#00E5FF]`}></i>
                </div>
                <h3 className="font-['Space_Grotesk'] text-xl font-bold mb-3">{item.title}</h3>
                <p className="text-white/70 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-['Barlow_Condensed'] text-5xl font-bold mb-6">
            Don't Wait for the First Storm
          </h2>
          <p className="font-['Space_Grotesk'] text-xl text-white/80 mb-10">
            Secure your winter snow management contract with Sure Kleen today.
          </p>
          <a
            href="/#contact"
            className="inline-block px-10 py-4 bg-gradient-to-r from-[#00E5FF] to-[#00B8D4] text-[#080808] font-['Space_Grotesk'] font-bold text-lg rounded-lg hover:shadow-[0_0_30px_rgba(0,229,255,0.5)] transition-all whitespace-nowrap"
          >
            Request Consultation
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <p className="font-['Space_Mono'] text-sm text-white/50">
            © 2026 Sure Kleen Services. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
